import { COMMA, ENTER } from "@angular/cdk/keycodes";
import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from "@angular/forms";
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from "@angular/material/autocomplete";
import { MatChipInputEvent, MatChipsModule } from "@angular/material/chips";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { TagService } from "@services/tag.service";
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from "rxjs";

@Component({
  selector: "fh-tag-autocomplete",
  templateUrl: "./tag-autocomplete.component.html",
  styleUrls: ["./tag-autocomplete.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TagAutocompleteComponent),
      multi: true,
    },
  ],
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatChipsModule,
    MatIconModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatInputModule,
  ],
})
export class TagAutocompleteComponent
  implements ControlValueAccessor, OnChanges, OnInit, OnDestroy
{
  @Input() existingValues: string[] = [];
  @Input() label = "Tags";
  @Input() type?: string | null;
  @Input() iso2?: string | null;
  @Output() selectionChange = new EventEmitter<string[]>();

  @ViewChild("tagInput") tagInput!: ElementRef<HTMLInputElement>;

  selectItems: string[] = [];
  filteredTags: string[] = [];
  tagInputControl = new FormControl("");
  readonly separatorKeysCodes: number[] = [ENTER, COMMA];
  addOnBlur = false; // Changed to false to prevent adding on blur when clicking options
  private destroy$ = new Subject<void>();
  private searchSubject = new Subject<string>();

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onTouched: () => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onChange: (value: string[]) => void = () => {};

  constructor(private tagService: TagService) {
    // Setup search debouncing
    this.searchSubject
      .pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((query) => {
        this.searchTags(query);
      });
  }

  ngOnInit(): void {
    if (this.existingValues) {
      this.selectItems = [...this.existingValues];
      this.onChange(this.selectItems);
    }

    // Watch input changes for autocomplete
    this.tagInputControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      if (typeof value === "string") {
        const trimmedValue = value.trim();
        if (trimmedValue.length > 0) {
          // Don't search if the value is already selected
          if (!this.selectItems.includes(trimmedValue)) {
            // Trigger API search
            this.searchSubject.next(trimmedValue);
          } else {
            this.filteredTags = [];
          }
        } else {
          // Clear suggestions when input is empty
          this.filteredTags = [];
        }
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.existingValues) {
      this.selectItems = changes.existingValues.currentValue || [];
      this.onChange(this.selectItems);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  writeValue(value: string[]): void {
    if (value) {
      this.selectItems = [...value];
    } else {
      this.selectItems = [];
    }
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setDisabledState(_isDisabled: boolean): void {
    // Handle the disabled state if necessary
  }

  searchTags(query: string): void {
    const trimmedQuery = query.trim();
    if (!trimmedQuery || trimmedQuery.length === 0) {
      this.filteredTags = [];
      return;
    }

    // Search API with max 25 results
    this.tagService
      .autocompleteTags({
        query: trimmedQuery,
        limit: 25, // Max 25 results
        type: this.type || null,
        iso2: this.iso2 || null,
      })
      .subscribe({
        next: (response) => {
          if (response.status && response.data && Array.isArray(response.data)) {
            // Filter out already selected tags
            this.filteredTags = response.data
              .map((tag: any) => tag.name)
              .filter((tag: string) => !this.selectItems.includes(tag));
          } else {
            this.filteredTags = [];
          }
        },
        error: () => {
          this.filteredTags = [];
        },
      });
  }

  onTagSelected(event: MatAutocompleteSelectedEvent): void {
    const tagName = (event.option.value || "").trim();
    if (tagName && !this.selectItems.includes(tagName)) {
      this.addTag(tagName);
      this.tagInputControl.setValue(null);
      if (this.tagInput?.nativeElement) {
        this.tagInput.nativeElement.value = "";
      }
      this.filteredTags = [];
    }
  }

  addTag(tagName: string): void {
    const trimmedTag = tagName.trim();
    if (trimmedTag && trimmedTag.length > 0 && !this.selectItems.includes(trimmedTag)) {
      this.selectItems.push(trimmedTag);
      this.onChange(this.selectItems);
      this.selectionChange.emit(this.selectItems);
    }
  }

  addOption(event: MatChipInputEvent): void {
    const value = (event.value || "").trim();

    if (value && value.length > 0 && !this.selectItems.includes(value)) {
      this.addTag(value);
    }

    // Clear the input value
    if (event.chipInput) {
      event.chipInput.clear();
    }
    this.tagInputControl.setValue(null);
    this.filteredTags = [];
  }

  removeOption(option: string): void {
    const index = this.selectItems.indexOf(option);

    if (index >= 0) {
      this.selectItems.splice(index, 1);
      this.onChange(this.selectItems);
      this.selectionChange.emit(this.selectItems);
    }
  }
}
